/**
 * Mobile Optimizer
 * Comprehensive mobile optimization and responsive enhancement utility
 */

import { ResponsiveManager } from './ResponsiveManager';
import { TouchGestureHandler } from './TouchGestureHandler';
import { MobileCarousel } from '../components/MobileCarousel';

interface MobileOptimizationConfig {
  enableTouchOptimizations: boolean;
  enableCarouselGestures: boolean;
  enablePerformanceOptimizations: boolean;
  enableAccessibilityEnhancements: boolean;
  touchTargetMinSize: number;
  swipeThreshold: number;
}

export class MobileOptimizer {
  private responsiveManager: ResponsiveManager;
  private touchHandlers: Map<HTMLElement, TouchGestureHandler> = new Map();
  private carousels: MobileCarousel[] = [];
  private config: MobileOptimizationConfig;
  private isInitialized: boolean = false;

  constructor(config: Partial<MobileOptimizationConfig> = {}) {
    this.config = {
      enableTouchOptimizations: config.enableTouchOptimizations ?? true,
      enableCarouselGestures: config.enableCarouselGestures ?? true,
      enablePerformanceOptimizations: config.enablePerformanceOptimizations ?? true,
      enableAccessibilityEnhancements: config.enableAccessibilityEnhancements ?? true,
      touchTargetMinSize: config.touchTargetMinSize ?? 44,
      swipeThreshold: config.swipeThreshold ?? 50
    };

    this.responsiveManager = ResponsiveManager.getInstance();
  }

  public async init(): Promise<void> {
    if (this.isInitialized) return;

    console.log('🚀 Initializing Mobile Optimizer...');

    // Initialize responsive manager
    this.responsiveManager.updateCSSCustomProperties();

    // Set up responsive callbacks
    this.setupResponsiveCallbacks();

    // Apply mobile optimizations
    if (this.config.enableTouchOptimizations) {
      await this.optimizeTouchTargets();
      this.setupTouchGestures();
    }

    // Initialize carousels with gestures
    if (this.config.enableCarouselGestures) {
      this.initializeCarousels();
    }

    // Apply performance optimizations
    if (this.config.enablePerformanceOptimizations) {
      this.applyPerformanceOptimizations();
    }

    // Enhance accessibility
    if (this.config.enableAccessibilityEnhancements) {
      this.enhanceAccessibility();
    }

    // Apply mobile-specific CSS optimizations
    this.applyCSSOptimizations();

    // Set up orientation change handling
    this.setupOrientationHandling();

    this.isInitialized = true;
    console.log('✅ Mobile Optimizer initialized successfully');
  }

  private setupResponsiveCallbacks(): void {
    this.responsiveManager.subscribe((state) => {
      // Update touch target sizes based on device
      if (state.isMobile) {
        this.updateTouchTargetSizes(this.config.touchTargetMinSize);
      }

      // Update carousel behavior
      this.updateCarouselBehavior(state);

      // Update navigation behavior
      this.updateNavigationBehavior(state);

      // Update form behavior
      this.updateFormBehavior(state);
    });
  }

  private async optimizeTouchTargets(): Promise<void> {
    const touchSelectors = [
      '.btn',
      '.form-input',
      '.form-select',
      '.header__nav-link',
      '.header__mobile-toggle',
      '.carousel-dot',
      '.carousel-arrow',
      'button',
      'a[href]',
      'input',
      'select',
      'textarea'
    ];

    touchSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      elements.forEach(element => {
        this.optimizeTouchTarget(element as HTMLElement);
      });
    });
  }

  private optimizeTouchTarget(element: HTMLElement): void {
    // Ensure minimum touch target size
    const rect = element.getBoundingClientRect();
    const minSize = this.config.touchTargetMinSize;

    if (rect.width < minSize || rect.height < minSize) {
      element.style.minWidth = `${minSize}px`;
      element.style.minHeight = `${minSize}px`;
      element.classList.add('touch-target-optimized');
    }

    // Add touch-friendly padding if needed
    const computedStyle = window.getComputedStyle(element);
    const paddingTop = parseFloat(computedStyle.paddingTop);
    const paddingBottom = parseFloat(computedStyle.paddingBottom);

    if (paddingTop + paddingBottom < 12) {
      element.style.padding = `${Math.max(6, paddingTop)}px ${computedStyle.paddingLeft}`;
    }

    // Improve touch feedback
    element.style.touchAction = 'manipulation';
    element.style.userSelect = 'none';
  }

  private setupTouchGestures(): void {
    // Add swipe gestures to navigation
    const nav = document.querySelector('.header__nav') as HTMLElement;
    if (nav) {
      const touchHandler = new TouchGestureHandler(nav, {
        swipe: { threshold: this.config.swipeThreshold }
      });

      touchHandler.onSwipe((direction) => {
        if (this.responsiveManager.isMobile()) {
          if (direction === 'left') {
            this.closeMobileMenu();
          } else if (direction === 'right') {
            this.openMobileMenu();
          }
        }
      });

      this.touchHandlers.set(nav, touchHandler);
    }

    // Add pull-to-refresh prevention
    document.body.addEventListener('touchstart', (e) => {
      if (e.touches.length !== 1) return;
      
      const touch = e.touches[0];
      if (!touch) return;
      
      const element = document.elementFromPoint(touch.clientX, touch.clientY);
      
      // Prevent pull-to-refresh on certain elements
      if (element?.closest('.search-form, .carousel, .filters')) {
        e.preventDefault();
      }
    }, { passive: false });
  }

  private initializeCarousels(): void {
    const carouselElements = document.querySelectorAll('.offers__grid, .results-grid');
    
    carouselElements.forEach(element => {
      if (this.responsiveManager.isMobile()) {
        const carousel = new MobileCarousel({
          container: element as HTMLElement,
          itemSelector: '.offer-card, .result-card',
          showDots: true,
          showArrows: false,
          swipeThreshold: this.config.swipeThreshold
        });
        
        this.carousels.push(carousel);
      }
    });
  }

  private updateTouchTargetSizes(minSize: number): void {
    const touchElements = document.querySelectorAll('.touch-target-optimized');
    touchElements.forEach(element => {
      (element as HTMLElement).style.minWidth = `${minSize}px`;
      (element as HTMLElement).style.minHeight = `${minSize}px`;
    });
  }

  private updateCarouselBehavior(state: any): void {
    this.carousels.forEach(carousel => {
      // Update carousel behavior based on screen size
      if (state.isMobile) {
        // Enable swipe gestures on mobile
        carousel.startAutoPlay();
      } else {
        // Disable auto-play on desktop
        carousel.stopAutoPlay();
      }
    });
  }

  private updateNavigationBehavior(state: any): void {
    const mobileToggle = document.querySelector('.header__mobile-toggle') as HTMLElement;
    const nav = document.querySelector('.header__nav') as HTMLElement;

    if (state.isMobile) {
      // Ensure mobile menu is properly configured
      if (mobileToggle) {
        mobileToggle.style.display = 'flex';
        mobileToggle.addEventListener('click', this.toggleMobileMenu.bind(this));
      }
      
      if (nav) {
        nav.classList.add('mobile-nav');
      }
    } else {
      // Desktop behavior
      if (mobileToggle) {
        mobileToggle.style.display = 'none';
      }
      
      if (nav) {
        nav.classList.remove('mobile-nav', 'mobile-nav--open');
      }
    }
  }

  private updateFormBehavior(state: any): void {
    const inputs = document.querySelectorAll('input, select, textarea');
    
    inputs.forEach(input => {
      const element = input as HTMLElement;
      
      if (state.isMobile) {
        // Prevent zoom on iOS
        element.style.fontSize = '16px';
        
        // Optimize input types for mobile keyboards
        if (input instanceof HTMLInputElement) {
          this.optimizeInputForMobile(input);
        }
      }
    });
  }

  private optimizeInputForMobile(input: HTMLInputElement): void {
    // Optimize input types for better mobile keyboards
    if (input.name?.includes('email') || input.placeholder?.toLowerCase().includes('email')) {
      input.type = 'email';
    } else if (input.name?.includes('phone') || input.placeholder?.toLowerCase().includes('phone')) {
      input.type = 'tel';
    } else if (input.name?.includes('url') || input.placeholder?.toLowerCase().includes('url')) {
      input.type = 'url';
    }

    // Add mobile-specific attributes
    input.setAttribute('autocomplete', 'on');
    input.setAttribute('autocorrect', 'off');
    input.setAttribute('autocapitalize', 'off');
    input.setAttribute('spellcheck', 'false');
  }

  private applyPerformanceOptimizations(): void {
    // Optimize images for mobile
    this.optimizeImages();
    
    // Reduce animations on low-end devices
    this.optimizeAnimations();
    
    // Implement efficient scrolling
    this.optimizeScrolling();
  }

  private optimizeImages(): void {
    const images = document.querySelectorAll('img');
    
    images.forEach(img => {
      // Add loading="lazy" if not already present
      if (!img.hasAttribute('loading') && !img.hasAttribute('data-lazy')) {
        img.loading = 'lazy';
      }

      // Add decoding="async" for better performance
      img.decoding = 'async';

      // Optimize image sizes for mobile
      if (this.responsiveManager.isMobile()) {
        const rect = img.getBoundingClientRect();
        if (rect.width > window.innerWidth) {
          img.style.maxWidth = '100%';
          img.style.height = 'auto';
        }
      }
    });
  }

  private optimizeAnimations(): void {
    // Reduce animations on low-end devices or when user prefers reduced motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const isLowEndDevice = navigator.hardwareConcurrency && navigator.hardwareConcurrency <= 2;

    if (prefersReducedMotion || isLowEndDevice) {
      document.documentElement.classList.add('reduced-motion');
      
      // Disable CSS animations
      const style = document.createElement('style');
      style.textContent = `
        .reduced-motion *,
        .reduced-motion *::before,
        .reduced-motion *::after {
          animation-duration: 0.01ms !important;
          animation-iteration-count: 1 !important;
          transition-duration: 0.01ms !important;
          scroll-behavior: auto !important;
        }
      `;
      document.head.appendChild(style);
    }
  }

  private optimizeScrolling(): void {
    // Enable smooth scrolling where appropriate
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      document.documentElement.style.scrollBehavior = 'smooth';
    }

    // Optimize touch scrolling on mobile
    if (this.responsiveManager.isMobile()) {
      document.body.style.overscrollBehavior = 'none';
      (document.body.style as any).webkitOverflowScrolling = 'touch';
    }
  }

  private enhanceAccessibility(): void {

    
    // Enhance focus management
    this.enhanceFocusManagement();
    
    // Add ARIA labels where missing
    this.addMissingAriaLabels();
    
    // Improve keyboard navigation
    this.improveKeyboardNavigation();
  }



  private enhanceFocusManagement(): void {
    // Ensure focus is visible on mobile
    const style = document.createElement('style');
    style.textContent = `
      @media (max-width: 767px) {
        *:focus {
          outline: 3px solid var(--primary-green) !important;
          outline-offset: 2px !important;
        }
      }
    `;
    document.head.appendChild(style);

    // Trap focus in mobile menu when open
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Tab') {
        this.handleTabNavigation(e);
      }
    });
  }

  private addMissingAriaLabels(): void {
    // Add aria-labels to buttons without text
    const buttons = document.querySelectorAll('button:not([aria-label]):not([aria-labelledby])');
    buttons.forEach(button => {
      if (!button.textContent?.trim()) {
        const className = button.className;
        if (className.includes('mobile-toggle')) {
          button.setAttribute('aria-label', 'Ouvrir le menu de navigation');
        } else if (className.includes('close')) {
          button.setAttribute('aria-label', 'Fermer');
        }
      }
    });

    // Add aria-labels to form inputs without labels
    const inputs = document.querySelectorAll('input:not([aria-label]):not([aria-labelledby])');
    inputs.forEach(input => {
      const placeholder = input.getAttribute('placeholder');
      if (placeholder && !document.querySelector(`label[for="${input.id}"]`)) {
        input.setAttribute('aria-label', placeholder);
      }
    });
  }

  private improveKeyboardNavigation(): void {
    // Ensure all interactive elements are keyboard accessible
    const interactiveElements = document.querySelectorAll('a, button, input, select, textarea, [tabindex]');
    
    interactiveElements.forEach(element => {
      if (!element.hasAttribute('tabindex') && element.tagName !== 'INPUT' && element.tagName !== 'SELECT' && element.tagName !== 'TEXTAREA') {
        element.setAttribute('tabindex', '0');
      }
    });
  }

  private applyCSSOptimizations(): void {
    const style = document.createElement('style');
    style.textContent = `
      /* Mobile-specific optimizations */
      @media (max-width: 767px) {
        /* Optimize font rendering */
        body {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
          text-rendering: optimizeLegibility;
        }
        
        /* Optimize touch scrolling */
        * {
          -webkit-overflow-scrolling: touch;
        }
        
        /* Prevent horizontal scroll */
        body {
          overflow-x: hidden;
        }
        
        /* Optimize button appearance */
        button, input[type="submit"], input[type="button"] {
          -webkit-appearance: none;
          appearance: none;
          border-radius: 8px;
        }
        
        /* Optimize select appearance */
        select {
          -webkit-appearance: none;
          appearance: none;
          background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 16 16'%3E%3Cpath fill='none' stroke='%23333' stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M2 5l6 6 6-6'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          background-size: 16px;
          padding-right: 40px;
        }
      }
    `;
    document.head.appendChild(style);
  }

  private setupOrientationHandling(): void {
    const handleOrientationChange = () => {
      // Update viewport height for mobile browsers
      if (this.responsiveManager.isMobile()) {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
      }

      // Update carousel layouts
      setTimeout(() => {
        this.carousels.forEach(_carousel => {
          // Trigger carousel resize
          window.dispatchEvent(new Event('resize'));
        });
      }, 100);
    };

    window.addEventListener('orientationchange', handleOrientationChange);
    window.addEventListener('resize', handleOrientationChange);
    
    // Initial call
    handleOrientationChange();
  }

  private toggleMobileMenu(): void {
    const nav = document.querySelector('.header__nav') as HTMLElement;
    const toggle = document.querySelector('.header__mobile-toggle') as HTMLElement;
    
    if (nav && toggle) {
      const isOpen = nav.classList.contains('mobile-nav--open');
      
      if (isOpen) {
        this.closeMobileMenu();
      } else {
        this.openMobileMenu();
      }
    }
  }

  private openMobileMenu(): void {
    const nav = document.querySelector('.header__nav') as HTMLElement;
    const toggle = document.querySelector('.header__mobile-toggle') as HTMLElement;
    
    if (nav && toggle) {
      nav.classList.add('mobile-nav--open');
      toggle.setAttribute('aria-expanded', 'true');
      toggle.setAttribute('aria-label', 'Fermer le menu de navigation');
      
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      
      // Focus first menu item
      const firstLink = nav.querySelector('a') as HTMLElement;
      if (firstLink) {
        firstLink.focus();
      }
    }
  }

  private closeMobileMenu(): void {
    const nav = document.querySelector('.header__nav') as HTMLElement;
    const toggle = document.querySelector('.header__mobile-toggle') as HTMLElement;
    
    if (nav && toggle) {
      nav.classList.remove('mobile-nav--open');
      toggle.setAttribute('aria-expanded', 'false');
      toggle.setAttribute('aria-label', 'Ouvrir le menu de navigation');
      
      // Restore body scroll
      document.body.style.overflow = '';
      
      // Return focus to toggle button
      toggle.focus();
    }
  }

  private handleTabNavigation(e: KeyboardEvent): void {
    const nav = document.querySelector('.header__nav') as HTMLElement;
    if (!nav || !nav.classList.contains('mobile-nav--open')) return;

    const focusableElements = nav.querySelectorAll('a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])');
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    if (e.shiftKey) {
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }

  public destroy(): void {
    // Clean up touch handlers
    this.touchHandlers.forEach(handler => handler.destroy());
    this.touchHandlers.clear();

    // Clean up carousels
    this.carousels.forEach(carousel => carousel.destroy());
    this.carousels = [];

    // Clean up responsive manager
    this.responsiveManager.destroy();

    this.isInitialized = false;
  }

  // Public API
  public getResponsiveManager(): ResponsiveManager {
    return this.responsiveManager;
  }

  public addTouchGestures(element: HTMLElement, options?: any): TouchGestureHandler {
    const handler = new TouchGestureHandler(element, options);
    this.touchHandlers.set(element, handler);
    return handler;
  }

  public removeTouchGestures(element: HTMLElement): void {
    const handler = this.touchHandlers.get(element);
    if (handler) {
      handler.destroy();
      this.touchHandlers.delete(element);
    }
  }

  public isMobile(): boolean {
    return this.responsiveManager.isMobile();
  }

  public isTablet(): boolean {
    return this.responsiveManager.isTablet();
  }

  public isDesktop(): boolean {
    return this.responsiveManager.isDesktop();
  }
}